import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, ExternalLink, Play } from 'lucide-react';
import { RESOURCE_PRODUCTS, assetUrl, formatDate, metaParts, primaryAction, typeInfo } from '../../lib/resources';

const ACTION_ICON = { file: Download, external: ExternalLink };

// One resource in the hub grid. The whole card opens the detail page; the action link does the main job directly
// (download the PDF, open the link). `featured` gives the navy treatment used in the top row.
const ResourceCard = ({ r, featured = false }) => {
  const { label, icon: Icon } = typeInfo(r.type);
  const action = primaryAction(r);
  const ActionIcon = r.type === 'video' || r.type === 'webinar' ? Play : (ACTION_ICON[action.kind] || ArrowRight);
  const meta = metaParts(r);
  const products = (r.products || []).slice(0, 3);
  const dark = featured;

  const actionClass = `relative z-10 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
    dark ? 'text-[#c8ff00] hover:text-white' : 'text-[#0a1f2e] hover:text-[#0a1f2e]/70'}`;

  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border transition-all duration-200 focus-within:ring-2 focus-within:ring-[#c8ff00] ${
        dark
          ? 'border-[#0a1f2e] bg-[#0a1f2e] text-white hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(10,31,46,0.6)]'
          : 'border-gray-200 bg-white hover:-translate-y-0.5 hover:border-[#0a1f2e]/30 hover:shadow-[0_12px_32px_-16px_rgba(10,31,46,0.35)]'
      }`}
    >
      {r.cover_image && (
        <div className={`aspect-[16/9] overflow-hidden ${dark ? 'bg-[#06141e] border-b border-white/10' : 'bg-[#f5f7f9]'}`}>
          <img src={assetUrl(r.cover_image)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex items-center gap-2.5">
          <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-xl ${dark ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'bg-[#0a1f2e] text-[#c8ff00]'}`}>
            <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
          </span>
          <span className={`text-xs font-semibold uppercase tracking-wider ${dark ? 'text-white/60' : 'text-gray-500'}`}>{label}</span>
        </div>
        <h3 className={`font-display text-lg font-semibold leading-snug ${dark ? 'text-white' : 'text-[#0a1f2e]'}`}>
          <Link to={`/resources/${r.slug}`} className="outline-none after:absolute after:inset-0 after:content-['']">
            {r.title}
          </Link>
        </h3>
        {r.summary && (
          <p className={`mt-2 line-clamp-3 text-sm leading-relaxed ${dark ? 'text-white/70' : 'text-gray-600'}`}>{r.summary}</p>
        )}
        {products.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {products.map((p) => (
              <span key={p} className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${dark ? 'bg-white/10 text-white/80' : 'bg-[#f5f7f9] text-gray-600'}`}>
                {RESOURCE_PRODUCTS[p] || p}
              </span>
            ))}
          </div>
        )}
        <div className={`mt-auto flex items-end justify-between gap-3 pt-5 ${dark ? '' : ''}`}>
          <p className={`text-xs ${dark ? 'text-white/55' : 'text-gray-500'}`}>
            {[...meta, !featured && r.updated_at ? `Updated ${formatDate(r.updated_at)}` : null].filter(Boolean).join(' · ')}
          </p>
          {action.kind === 'file' || action.kind === 'external' ? (
            <a href={action.href} target="_blank" rel="noopener noreferrer" className={`${actionClass} flex-none`}>
              <ActionIcon className="h-4 w-4" aria-hidden="true" /> {action.label}
            </a>
          ) : (
            <span className={`${actionClass} flex-none`} aria-hidden="true">
              {action.label} <ActionIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default ResourceCard;
